import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, PieChart, Target, BarChart2 } from 'lucide-react';

interface CalculatorCardProps {
  title: string;
  description: string;
  link: string;
  icon: React.ElementType;
}

const GrowthMarketSharePage = () => {
  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/growth-market-share`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Growth & Market Share Calculators | Free Marketing Tools</title>
        <meta name="description" content="Calculate growth rates, market share, penetration rates & more with our free calculators. Make data-driven decisions for your business growth." />
        <meta name="keywords" content="growth rate calculator, market share calculator, market penetration rate, market concentration calculator" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Growth & Market Share Calculators" />
        <meta property="og:description" content="Calculate and track your business growth metrics with our free calculators. Measure market share, growth rates, and more." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Growth & Market Share Calculators" />
        <meta property="twitter:description" content="Calculate and track your business growth metrics with our free calculators. Measure market share, growth rates, and more." />
        <meta property="twitter:image" content={logoUrl} />
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6">Growth & Market Share</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Growth & Market Share Calculators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <CalculatorCard 
            title="Growth Rate" 
            description="Calculate the rate of growth over a specific period" 
            link="/calculators/growth-rate" 
            icon={TrendingUp}
          />
          <CalculatorCard 
            title="Market Share" 
            description="Calculate your company's percentage of total market sales" 
            link="/calculators/market-share" 
            icon={PieChart}
          />
          <CalculatorCard 
            title="Market Penetration Rate" 
            description="Measure the portion of a market your company has captured" 
            link="/calculators/market-penetration-rate" 
            icon={Target}
          />
          <CalculatorCard 
            title="Market Concentration" 
            description="Calculate market concentration using the Herfindahl-Hirschman Index (HHI)" 
            link="/calculators/market-concentration" 
            icon={BarChart2}
          />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-700 mb-4">Understanding Growth & Market Share Metrics</h2>
        <p className="text-gray-600 mb-4">
          Tracking growth and market share metrics is essential for understanding your company's position in the market and identifying opportunities for expansion. Use these calculators to measure and analyze your market performance.
        </p>
      </section>
    </div>
  );
};

const CalculatorCard: React.FC<CalculatorCardProps> = ({ title, description, link, icon: Icon }) => (
  <Link to={link} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
    <Icon className="w-8 h-8 text-indigo-600 mb-4" />
    <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 mb-4">{description}</p>
    <span className="text-indigo-600 font-medium">Use Calculator</span>
  </Link>
);

export default GrowthMarketSharePage;
